import { createTheme } from "@mui/material/styles";
import { darkEffects } from "@themes/tokens/effects/dark";
import { lightEffects } from "@themes/tokens/effects/light";
import { darkUiTokens } from "@themes/tokens/ui/darkUi";
import { lightUiTokens } from "@themes/tokens/ui/lightUi";

import { IS_TEST_ENV } from "../app/bootstrap/init";
import createComponentOverrides from "./overrides";
import { buildChartSeries, toChartSeriesTokens } from "./tokens/chartSeries";
import {
  DEFAULT_PRIMARY_PALETTE,
  PRIMARY_PALETTES,
  PrimaryPaletteKey
} from "./tokens/primaryPalettes";
import { darkPalette } from "./tokens/dark";
import { lightPalette } from "./tokens/light";
import { controls, shape, typography } from "./tokens/shared";

/**
 * Builds the app theme for a selected primary palette.
 * Only `palette.primary` varies between selectable palettes.
 */
export const createAppTheme = (
  primaryPalette: PrimaryPaletteKey = DEFAULT_PRIMARY_PALETTE
) => {
  const definition = PRIMARY_PALETTES[primaryPalette];

  /**
   * The categorical chart ramp, derived per scheme from the palette that is
   * actually selected. See `tokens/chartSeries.ts` for the territories.
   */
  const chart = {
    dark: {
      series: toChartSeriesTokens(buildChartSeries(primaryPalette, "dark"))
    },
    light: {
      series: toChartSeriesTokens(buildChartSeries(primaryPalette, "light"))
    }
  } as const;

  return createTheme({
    // e2e only: make MUI transitions no-ops so popovers/menus render at their
    // final position immediately. Under CPU-starved headless runs the Menu Grow
    // animation can stay "not stable" for seconds, timing out Playwright clicks.
    ...(IS_TEST_ENV ? { transitions: { create: () => "none" } } : {}),

    colorSchemes: {
      dark: {
        appEffects: darkEffects,
        chart: chart.dark,
        palette: {
          ...darkPalette,
          primary: definition.dark.primary
        },
        ui: darkUiTokens
      },

      light: {
        appEffects: lightEffects,
        chart: chart.light,
        palette: {
          ...lightPalette,
          primary: definition.light.primary
        },
        ui: lightUiTokens
      }
    },

    components: createComponentOverrides(),

    controls,

    cssVariables: {
      colorSchemeSelector: "data"
    },

    shape,
    typography
  });
};

export default createAppTheme;
